import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { authApi } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Lock, LogIn, UserPlus } from "lucide-react";

function loginWithGoogle() {
  const url = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(url)}`;
}

function detailToString(d) {
  if (!d) return "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d?.msg || String(d);
}

export default function AuthModal({ open, onOpenChange, defaultMode = "login" }) {
  const [mode, setMode] = useState(defaultMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [stateV, setStateV] = useState("");
  const [phone, setPhone] = useState("");
  const [referredBy, setReferredBy] = useState("");
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [busy, setBusy] = useState(false);

  // Sync mode with the incoming defaultMode when the modal is opened,
  // so parent buttons ("Sign in" vs "Sign up") always land on the right form.
  useEffect(() => {
    if (open) setMode(defaultMode);
  }, [open, defaultMode]);

  const submit = async () => {
    if (!email || !password) return toast.error("Email and password required");
    try {
      setBusy(true);
      if (mode === "login") {
        await authApi.login({ email, password });
        toast.success("Welcome back");
        window.location.href = "/dashboard";
      } else if (mode === "register") {
        if (!fullName.trim() || !address.trim() || !city.trim() || !stateV.trim() || !phone.trim())
          return toast.error("Please complete every required field.");
        if (!ageConfirmed) return toast.error("You must confirm you are 18 or older.");
        if (!termsAccepted) return toast.error("You must acknowledge the terms & disclaimer.");
        const r = await authApi.register({
          email, password,
          name: fullName, full_name: fullName,
          address, city, state: stateV, phone,
          referred_by: referredBy || null,
          age_confirmed: ageConfirmed, terms_accepted: termsAccepted,
        });
        toast.success(r.verification_sent ? "Account created — check your email to verify" : "Account created");
        window.location.href = "/dashboard";
      } else if (mode === "forgot") {
        await authApi.forgot(email);
        toast.success("If that account exists, a reset link is on its way");
        setMode("login");
      }
    } catch (e) {
      toast.error(detailToString(e?.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const title = mode === "register" ? "Create your TradingoX account" : mode === "forgot" ? "Reset your password" : "Welcome back";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="auth-modal" className={`bg-[#12141D] border-white/10 text-slate-100 ${mode === "register" ? "max-w-lg max-h-[90vh] overflow-y-auto" : "max-w-md"}`}>
        <DialogHeader>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-300">
            {mode === "register" ? "Sign up" : mode === "forgot" ? "Forgot password" : "Sign in"}
          </div>
          <DialogTitle className="font-display text-2xl font-extrabold mt-1">{title}</DialogTitle>
        </DialogHeader>

        <Button onClick={loginWithGoogle} className="w-full h-11 rounded-full bg-white text-black hover:bg-slate-200 font-medium">
          Continue with Google
        </Button>
        <div className="flex items-center gap-3 my-2">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-slate-500">or email</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        <div className="space-y-2">
          {mode === "register" && (
            <>
              <Input data-testid="auth-full-name" placeholder="Full name *" value={fullName}
                onChange={(e) => setFullName(e.target.value)} className="bg-[#0D0E14] border-white/10 h-11" />
              <Input data-testid="auth-address" placeholder="Street address *" value={address}
                onChange={(e) => setAddress(e.target.value)} className="bg-[#0D0E14] border-white/10 h-11" />
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="auth-city" placeholder="City *" value={city}
                  onChange={(e) => setCity(e.target.value)} className="bg-[#0D0E14] border-white/10 h-11" />
                <Input data-testid="auth-state" placeholder="State *" value={stateV}
                  onChange={(e) => setStateV(e.target.value)} className="bg-[#0D0E14] border-white/10 h-11" />
              </div>
              <Input data-testid="auth-phone" type="tel" placeholder="Phone number *" value={phone}
                onChange={(e) => setPhone(e.target.value)} className="bg-[#0D0E14] border-white/10 h-11" />
              <Input data-testid="auth-referred-by" placeholder="Who referred you? (optional)" value={referredBy}
                onChange={(e) => setReferredBy(e.target.value)} className="bg-[#0D0E14] border-white/10 h-11" />
            </>
          )}
          <div className="relative">
            <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input data-testid="auth-email" type="email" placeholder={mode === "register" ? "Email address *" : "you@example.com"} value={email}
              onChange={(e) => setEmail(e.target.value)} className="bg-[#0D0E14] border-white/10 h-11 pl-9" />
          </div>
          {mode !== "forgot" && (
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input data-testid="auth-password" type="password" placeholder="Password (8+ chars)" value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submit()}
                className="bg-[#0D0E14] border-white/10 h-11 pl-9" />
            </div>
          )}
        </div>

        {mode === "register" && (
          <div className="space-y-2 pt-2 border-t border-white/5 mt-2">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                data-testid="auth-age-check"
                type="checkbox"
                checked={ageConfirmed}
                onChange={(e) => setAgeConfirmed(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded accent-emerald-500 cursor-pointer"
              />
              <span className="text-[12px] text-slate-300 leading-snug">
                I confirm that I am <b className="text-emerald-300">18 years or older</b>.
              </span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                data-testid="auth-terms-check"
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded accent-emerald-500 cursor-pointer"
              />
              <span className="text-[12px] text-slate-300 leading-snug">
                I have read and understand the TradingoX{" "}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="auth-terms-link"
                  onClick={(e) => e.stopPropagation()}
                  className="text-emerald-300 font-semibold underline underline-offset-2 hover:text-emerald-200"
                >
                  terms, conditions, warning, and disclaimer
                </a>.
              </span>
            </label>
          </div>
        )}

        <Button data-testid="auth-submit" onClick={submit} disabled={busy}
          className="w-full h-11 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">
          {busy ? "…" : mode === "register" ? <><UserPlus className="w-4 h-4 mr-2"/>Create account</> :
                    mode === "forgot" ? "Send reset link" : <><LogIn className="w-4 h-4 mr-2"/>Sign in</>}
        </Button>

        <div className="text-xs text-slate-400 flex items-center justify-between pt-1">
          {mode === "login" && (
            <>
              <button className="hover:text-white" onClick={() => setMode("forgot")}>Forgot password?</button>
              <button className="hover:text-white" onClick={() => setMode("register")}>Create account →</button>
            </>
          )}
          {mode === "register" && (
            <button className="hover:text-white ml-auto" onClick={() => setMode("login")}>Already have an account? Sign in</button>
          )}
          {mode === "forgot" && (
            <button className="hover:text-white ml-auto" onClick={() => setMode("login")}>← Back to sign in</button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
